<template>
  <TableList
    v-rect-loading="loading"
    class="source-code-page"
    row-key="id"
    :title="$t(`${i18nPrefix}.tab.sourceCodeManagement`)"
    :listData="sourceCodeList"
    :tableColumns="tableColumns"
    :pagination="pagination"
    :actionBtn="actionBtn"
    @create="handelCreateSourceCode"
  >
    <template #filter>
      <div class="header-filter">
        <span class="company-select">
          <el-select v-model="selectedCompanyId" filterable @change="handleCompanySelect">
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="item.companyName"
              :style="{ paddingLeft: item.level * 15 + 'px' }"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-filter">
        <span class="status-select">
          <el-select v-model="selectedStatus" @change="handleStatusSelect">
            <el-option
              v-for="item in statusList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
            </el-option>
          </el-select>
        </span>
      </div>
      <div class="header-filter">
        <el-input
          v-model="keyword"
          class="search-input"
          clearable
          :title="$t(`${i18nPrefix}.placeholder.search`)"
          :placeholder="$t(`${i18nPrefix}.placeholder.search`)"
          @input="handleKeywordsChange"
        >
        </el-input>
      </div>
    </template>
    <template #selectableHeader="{ column}">
      <div v-auto-tooltip.always="{ content: $t(`${i18nPrefix}.tip.selectableTip`) }" class="selectable-header">
        <span class="label">{{ column.label }}</span>
        <i class="tip iconfont iconquestion-mark"></i>
      </div>
    </template>
    <template #selectable="{ data, row }">
      <el-switch :value="!data" @change="onStatusChange($event, row)"></el-switch>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { mapActions, mapGetters } from 'vuex'
import { addI18nProperty } from '@/i18n'

export default {
  components: {
    TableList
  },
  props: {

  },
  data () {
    return {
      i18nPrefix: 'admin.company.sourceChannelManagement',
      loading: false,
      allSourceCodeList: [],
      companyList: [],
      selectedCompanyId: null,
      tempCompanyId: null,
      selectedStatus: 0,
      keyword: null,
      keywordsSearchDebounce: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      actionBtn: {
        edit: {
          show: true,
          onclick: this.handleEditSourceCode
        }
      }
    }
  },
  computed: {
    account () {
      return this.getCurrentAccount()
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.source`),
          dataKey: 'source',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.code`),
          dataKey: 'code',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'channel.id',
          formatter: (data) => {
            return this.$t(`${this.i18nPrefix}.label.${data ? 'classified' : 'unclassified'}`)
          },
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.channel`),
          dataKey: 'channel.i18nName',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.selectable`),
          dataKey: 'hide_on_page',
          headerSlotName: 'selectableHeader',
          slotName: 'selectable'
        }
      ]
    },
    statusList () {
      return [
        { value: 0, label: this.$t(`${this.i18nPrefix}.label.all`) },
        { value: 1, label: this.$t(`${this.i18nPrefix}.label.classified`) },
        { value: 2, label: this.$t(`${this.i18nPrefix}.label.unclassified`) }
      ]
    },
    queryCompany () {
      return this.$route.query.company
    },
    queryKeyword () {
      return this.$route.query.keyword
    },
    queryStatus () {
      return this.$route.query.status
    },
    sourceCodeList () {
      const sourceCodeList = [...this.allSourceCodeList]
      const startIndex = (this.pagination.page - 1) * this.pagination.pageSize
      return sourceCodeList.splice(startIndex, this.pagination.pageSize)
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    ...mapActions('company', ['getCompanyChildrenTree']),
    ...mapActions('sourceCode', ['getSourceCodeList', 'updateSourceCodeById']),
    async initData () {
      await this.loadCompanyList()
      if (!this.queryCompany) {
        return this.$router.replace({ query: { ...this.$route.query, company: this.selectedCompanyId } })
      }
      await this.loadSourceCodeList(true)
    },
    async loadCompanyList () {
      this.loading = true
      try {
        const companyTreeResp = await this.getCompanyChildrenTree()
        const currentCompany = {
          id: companyTreeResp.self.id,
          name: companyTreeResp.self.name,
          cn_name: companyTreeResp.self.cn_name,
          level: companyTreeResp.self.level
        }
        const companyList = []
        this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
        if (this.tempCompanyId) {
          const company = _.find(this.companyList, { id: this.tempCompanyId })
          if (company) {
            this.selectedCompanyId = company.id
            return
          }
        }
        this.selectedCompanyId = currentCompany.id
      } finally {
        this.loading = false
      }
    },
    async loadSourceCodeList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      const params = {
        classify: this.selectedStatus,
        company_id: this.selectedCompanyId,
        keyword: this.keyword,
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }
      this.loading = true
      try {
        const resp = await this.getSourceCodeList(params)
        if (resp) {
          const vm = this
          this.allSourceCodeList = _.chain(resp).forEach(item => {
            if (!item.channel) {
              item.channel = {
                id: item.classify ? -1 : null,
                get i18nName () {
                  return vm.$t(`${vm.i18nPrefix}.label.weChatChannel`)
                }
              }
            } else {
              addI18nProperty(item.channel, 'i18nName', 'cn_name', 'name')
            }
          }).orderBy(['id'], ['desc']).value()
          this.pagination.total = this.allSourceCodeList.length
        }
      } finally {
        this.loading = false
      }
    },
    handleEditSourceCode (row) {
      this.$router.push({ path: `/v-admin/source-channel/management/code/edit/${row.id}`, query: this.$route.query })
    },
    handleCompanySelect () {
      this.$router.push({ query: { ...this.$route.query, company: this.selectedCompanyId } })
    },
    async onStatusChange (status, row) {
      this.loading = true
      try {
        const data = {
          sourceCodeId: row.id,
          data: {
            company_id: this.selectedCompanyId,
            hide_on_page: !status
          }
        }
        const resp = await this.updateSourceCodeById(data)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          row.hide_on_page = !status
        }
      } finally {
        this.loading = false
      }
    },
    handelCreateSourceCode () {
      this.$router.push({ path: '/v-admin/source-channel/management/code/add', query: this.$route.query })
    },
    handleStatusSelect () {
      this.$router.push({ query: { ...this.$route.query, status: this.selectedStatus } })
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleSearch, 500)
      }
      this.keywordsSearchDebounce()
    },
    handleSearch () {
      this.$router.push({ query: { ...this.$route.query, keyword: this.keyword } })
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'companyName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        _.forEach(children, (child) => {
          this.formatCompanyData(data, child, companyList)
        })
      }
      return companyList
    }
  },
  watch: {
    $route: {
      immediate: true,
      async handler () {
        const companyId = this.queryCompany
        this.keyword = this.queryKeyword
        const selectedStatus = parseInt(this.queryStatus)
        this.selectedStatus = _.isNaN(selectedStatus) ? 0 : selectedStatus
        if (!companyId) {
          return
        }
        if (this.companyList && this.companyList.length > 0) {
          this.selectedCompanyId = parseInt(companyId)
          this.loadSourceCodeList(true)
        } else {
          this.tempCompanyId = parseInt(companyId)
        }
      }
    }
  }

}
</script>

<style lang="scss" scoped>
.source-code-page {
  padding: 0;

  .header-filter {
    display: inline-block;
    vertical-align: middle;
    margin-bottom: 10px;

    .text {
      margin-right: 10px;
      display: inline-block;
      vertical-align: middle;
    }

    .company-select {
      display: inline-block;
      vertical-align: middle;
    }

    .status-select {
      display: inline-block;
      vertical-align: middle;
    }

    ::v-deep {
      .el-input__inner {
        border: 1px solid #c0c4cc;
      }

      .el-input-group__append {
        border-left: none;
        padding: 0 5px;
        background-color: #fff;
      }
    }

    .icon-search {
      cursor: pointer;
      font-size: 18px;
    }
  }

  .selectable-header {
    display: inline;
    cursor: help;

    .tip {
      position: relative;
      top: -2px;
      font-size: 16px;
    }
  }

  ::v-deep {
    .list-head {
      height: auto;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin-bottom: 10px;

      .title {
        margin-bottom: 10px;
      }

      .btn-container {
        display: flex;
        margin-left: 10px;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
